import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fmtEUR } from "@/lib/api";
import { STATUSES, STATUS_BADGE, STATUS_DOT } from "@/lib/statuses";
import {
  FileText, Users, Activity, ArrowUpRight, Plus,
} from "lucide-react";
import StatusPill from "@/components/StatusPill";

const StatCard = ({ label, value, icon: Icon, testid, accent = false }) => (
  <div
    data-testid={testid}
    className={`relative overflow-hidden rounded-xl border ${accent ? "border-zinc-700 bg-[#141416]" : "border-zinc-800 bg-[#121214]"} p-6 transition-all hover:-translate-y-0.5 hover:border-zinc-600`}
  >
    <div className="flex items-start justify-between mb-6">
      <div className="w-10 h-10 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center">
        <Icon className="w-[18px] h-[18px] text-zinc-300" strokeWidth={1.5} />
      </div>
      <ArrowUpRight className="w-4 h-4 text-zinc-600" strokeWidth={1.5} />
    </div>
    <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-1.5">{label}</div>
    <div className="font-display text-3xl font-black text-zinc-50 tracking-tighter">{value}</div>
  </div>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const [s, q] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/quotes"),
        ]);
        setStats(s.data);
        setQuotes(q.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="max-w-7xl mx-auto fade-up" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-4 mb-10 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-2">Visão Geral · 2026</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-zinc-50 tracking-tighter">
            Dashboard <span className="text-silver-gradient">BillMerch</span>
          </h1>
          <p className="text-zinc-500 mt-2 text-sm max-w-xl">
            Acompanhe todas as cotações, encomendas e produção em um único painel.
          </p>
        </div>
        <button
          onClick={() => navigate("/cotacoes/nova")}
          data-testid="dashboard-new-quote-btn"
          className="flex items-center gap-2 px-5 py-3 bg-zinc-100 hover:bg-white text-zinc-900 font-bold rounded-lg text-sm transition-all shadow-[0_0_20px_rgba(255,255,255,0.06)]"
        >
          <Plus className="w-4 h-4" strokeWidth={2.2} /> Nova Cotação
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard label="Cotações" value={stats?.total_quotes ?? "—"} icon={FileText} testid="stat-quotes" />
        <StatCard label="Clientes" value={stats?.total_clients ?? "—"} icon={Users} testid="stat-clients" />
        <StatCard
          label="Em Produção"
          value={stats?.by_status?.em_producao ?? "—"}
          icon={Activity}
          testid="stat-production"
        />
        <StatCard
          label="Receita Confirmada"
          value={stats ? fmtEUR(stats.confirmed_revenue) : "—"}
          icon={ArrowUpRight}
          testid="stat-revenue"
          accent
        />
      </div>

      {/* Pipeline overview */}
      <div className="mb-10">
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-1">Funil de Vendas</div>
            <h2 className="font-display text-2xl font-bold text-zinc-100 tracking-tight">Distribuição por estágio</h2>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {STATUSES.map((s) => (
            <div
              key={s.key}
              data-testid={`pipeline-stat-${s.key}`}
              className="rounded-xl border border-zinc-800 bg-[#121214] p-4 hover:border-zinc-700 transition-all"
            >
              <div className="flex items-center gap-2 mb-3">
                <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[s.key]}`} />
                <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">{s.short}</span>
              </div>
              <div className="font-display text-2xl font-black text-zinc-100 tracking-tighter">
                {stats?.by_status?.[s.key] ?? 0}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* All quotes table */}
      <div>
        <div className="flex items-end justify-between mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-1">Operação</div>
            <h2 className="font-display text-2xl font-bold text-zinc-100 tracking-tight">Todas as Cotações</h2>
          </div>
          <button
            onClick={() => navigate("/cotacoes")}
            className="text-sm text-zinc-400 hover:text-zinc-100 transition-colors"
          >
            Ver kanban →
          </button>
        </div>

        <div className="rounded-xl border border-zinc-800 bg-[#121214] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0e0e10] border-b border-zinc-800">
                <tr className="text-left text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">
                  <th className="px-5 py-3.5">Nº</th>
                  <th className="px-5 py-3.5">Cliente</th>
                  <th className="px-5 py-3.5">Itens</th>
                  <th className="px-5 py-3.5">Total</th>
                  <th className="px-5 py-3.5">Estágio</th>
                  <th className="px-5 py-3.5">Atualizada</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan="6" className="px-5 py-10 text-center text-zinc-500">Carregando...</td></tr>
                )}
                {!loading && quotes.length === 0 && (
                  <tr><td colSpan="6" className="px-5 py-10 text-center text-zinc-500" data-testid="dashboard-empty-state">
                    Sem cotações ainda. Crie a primeira!
                  </td></tr>
                )}
                {quotes.map((q) => (
                  <tr
                    key={q.id}
                    onClick={() => navigate(`/cotacoes/${q.id}`)}
                    data-testid={`dashboard-quote-row-${q.id}`}
                    className="border-b border-zinc-900 last:border-0 hover:bg-zinc-900/40 cursor-pointer transition-colors"
                  >
                    <td className="px-5 py-4 font-mono text-zinc-300 font-medium">{q.number}</td>
                    <td className="px-5 py-4 text-zinc-100">{q.client?.name || "—"}</td>
                    <td className="px-5 py-4 text-zinc-400">{q.items?.length || 0} item(s)</td>
                    <td className="px-5 py-4 text-zinc-100 font-medium">
                      {q.confirmed_total != null
                        ? fmtEUR(q.confirmed_total)
                        : q.total_range && q.total_range.min !== q.total_range.max
                          ? `${fmtEUR(q.total_range.min)} — ${fmtEUR(q.total_range.max)}`
                          : fmtEUR(q.total_range?.max ?? q.total)}
                    </td>
                    <td className="px-5 py-4">
                      <StatusPill status={q.status} />
                    </td>
                    <td className="px-5 py-4 text-zinc-500 text-xs">
                      {new Date(q.updated_at).toLocaleDateString("pt-BR")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
